/** Stream parsing for the relay's SSE (/subscribe) and NDJSON (/backfill).
 *  Works over any ReadableStream<Uint8Array> — browser fetch body or a test stream. */

/** Yield text lines from a byte stream; a trailing non-empty partial is emitted at end. */
export async function* lineFrames(stream: ReadableStream<Uint8Array>): AsyncGenerator<string> {
  const reader = stream.getReader();
  const dec = new TextDecoder();
  let buf = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let nl: number;
      while ((nl = buf.indexOf("\n")) >= 0) {
        yield buf.slice(0, nl).replace(/\r$/, "");
        buf = buf.slice(nl + 1);
      }
    }
    buf += dec.decode();
    if (buf.length > 0) yield buf.replace(/\r$/, "");
  } finally {
    reader.releaseLock();
  }
}

export interface SseFrame {
  event: string;
  data: string;
}

/** Parse W3C SSE frames: `event:`/`data:` field lines, blank line dispatches. */
export async function* sseEvents(stream: ReadableStream<Uint8Array>): AsyncGenerator<SseFrame> {
  let event = "message";
  const data: string[] = [];
  for await (const line of lineFrames(stream)) {
    if (line === "") {
      if (data.length > 0) yield { event, data: data.join("\n") };
      event = "message";
      data.length = 0;
      continue;
    }
    if (line.startsWith(":")) continue; // comment/keepalive
    const colon = line.indexOf(":");
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
  }
  if (data.length > 0) yield { event, data: data.join("\n") };
}
